import type { NoticeVariable } from '../types/variables'
import type { ChannelsState } from '../types/composer'

/** Class applied to every variable chip inside the editors. */
export const VAR_CLASS = 'mp-var'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * The chip markup inserted into an editor. It is `contenteditable="false"` so
 * the browser treats it as a single atomic character: one backspace removes the
 * whole variable rather than half of its name.
 */
export function variableTokenHtml(
  variable: NoticeVariable,
  lang: 'fr' | 'en',
): string {
  return `<span class="${VAR_CLASS}" data-var="${escapeHtml(
    variable.id,
  )}" data-kind="${variable.kind}" contenteditable="false">${escapeHtml(
    variable.label[lang],
  )}</span>`
}

/** The value a variable renders as, in the given content language. */
export function resolveVariable(
  variable: NoticeVariable,
  contentLang: 'fr' | 'en',
): string {
  if (variable.auto === 'date') {
    return new Date().toLocaleDateString(
      contentLang === 'fr' ? 'fr-CA' : 'en-CA',
      { day: 'numeric', month: 'long', year: 'numeric' },
    )
  }
  const value = variable.value[contentLang].trim()
  if (value) return value
  return variable.fallback?.[contentLang].trim() ?? ''
}

/** Replaces every chip in the HTML with its resolved value. */
export function resolveHtml(
  html: string,
  variables: NoticeVariable[],
  contentLang: 'fr' | 'en',
): string {
  if (!html) return ''
  const root = document.createElement('div')
  root.innerHTML = html
  root.querySelectorAll(`span.${VAR_CLASS}`).forEach((node) => {
    const id = node.getAttribute('data-var')
    const variable = variables.find((v) => v.id === id)
    const text = variable
      ? resolveVariable(variable, contentLang)
      : (node.textContent ?? '')
    node.replaceWith(document.createTextNode(text))
  })
  return root.innerHTML
}

/** Plain text of the content once every variable has been resolved. */
export function resolveText(
  html: string,
  variables: NoticeVariable[],
  contentLang: 'fr' | 'en',
): string {
  const root = document.createElement('div')
  root.innerHTML = resolveHtml(html, variables, contentLang)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
  return (root.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
}

/** Character count of the resolved content — what the citizen actually receives. */
export function resolvedLength(
  html: string,
  variables: NoticeVariable[],
  contentLang: 'fr' | 'en',
): number {
  return resolveText(html, variables, contentLang).length
}

/** Every variable id referenced by a piece of content. */
function idsIn(html: string): string[] {
  if (!html) return []
  const root = document.createElement('div')
  root.innerHTML = html
  return Array.from(root.querySelectorAll(`span.${VAR_CLASS}`))
    .map((node) => node.getAttribute('data-var') ?? '')
    .filter(Boolean)
}

/** The channel fields scanned for variable usage, with their display labels. */
const CHANNEL_FIELDS: {
  key: keyof ChannelsState
  label: { fr: string; en: string }
  read: (channels: ChannelsState) => string[]
}[] = [
  {
    key: 'email',
    label: { fr: 'Courriel', en: 'Email' },
    read: (c) => [c.email.subject, c.email.body],
  },
  { key: 'sms', label: { fr: 'SMS', en: 'SMS' }, read: (c) => [c.sms.text] },
  {
    key: 'voice',
    label: { fr: 'Voix', en: 'Voice' },
    read: (c) => [c.voice.script],
  },
  {
    key: 'push',
    label: { fr: 'Push', en: 'Push' },
    read: (c) => [c.push.title, c.push.message],
  },
  {
    key: 'website',
    label: { fr: 'Site web', en: 'Website' },
    read: (c) => [c.website.bannerText, c.website.ctaLabel],
  },
  {
    key: 'social',
    label: { fr: 'Réseaux sociaux', en: 'Social media' },
    read: (c) => [c.social.postText],
  },
  {
    key: 'screens',
    label: { fr: 'Écrans publics', en: 'Public screens' },
    read: (c) => [c.screens.headline, c.screens.bodyText],
  },
]

export interface VariableUsage {
  /** Number of chips found across every channel. */
  count: number
  /** Names of the channels using the variable, so the author knows where to look. */
  channels: string[]
}

/**
 * Where a variable is currently used. Deleting a variable is blocked while this
 * returns a non-zero count, and the channel names are shown to the author.
 */
export function getVariableUsage(
  variableId: string,
  channels: ChannelsState,
  lang: 'fr' | 'en',
): VariableUsage {
  let count = 0
  const used: string[] = []
  CHANNEL_FIELDS.forEach((field) => {
    const hits = field
      .read(channels)
      .reduce((sum, html) => sum + idsIn(html).filter((id) => id === variableId).length, 0)
    if (hits > 0) {
      count += hits
      used.push(field.label[lang])
    }
  })
  return { count, channels: used }
}

/** Custom variables with no value for the language currently being written. */
export function getIncompleteVariables(
  variables: NoticeVariable[],
  channels: ChannelsState,
  contentLang: 'fr' | 'en',
): NoticeVariable[] {
  return variables.filter(
    (v) =>
      v.kind === 'custom' &&
      !v.value[contentLang].trim() &&
      getVariableUsage(v.id, channels, contentLang).count > 0,
  )
}
